import { useEffect, useMemo, useRef, useState } from 'react';
import type { RenderedOutput } from '../../../api/render.js';
import { EmbedHtmlViewer } from './EmbedHtmlViewer.js';
import {
  clampFrameHeight,
  clampFrameWidth,
  isFrameMessageFrom,
  parseSageFrameMessage,
  UNTRUSTED_FRAME_SANDBOX,
} from '../../../lib/untrusted-frame.js';

export interface PaperGeometry {
  /** 'portrait' | 'landscape' */
  readonly orientation: 'portrait' | 'landscape';
  /** 'letter' | 'legal' | 'a4' | 'tabloid' */
  readonly size: 'letter' | 'legal' | 'a4' | 'tabloid';
}

/**
 * Polymorphic inline viewer. Picks a sub-viewer based on the
 * rendered output's format: PDF → embedded iframe with browser viewer;
 * CSV → sortable table; JSON → collapsible tree; HTML → sandboxed
 * iframe; XLSX → "download" fallback with a thumbnail teaser
 * (SheetJS would work but it's too heavy to ship by default).
 *
 * The caller owns the RenderedOutput lifecycle including blob-URL
 * disposal. This viewer is a pure consumer — earlier versions revoked
 * on unmount, but that broke the dual-mount case (panel + fullscreen
 * modal both showing the same output): closing the modal revoked the
 * URL out from under the panel's iframe, producing
 * "Not allowed to load local resource: blob:…" on the next reflow.
 */
export function InlineRenderViewer({
  output,
  height = 720,
  paper,
  reportTitle,
}: {
  output: RenderedOutput;
  height?: number;
  /** Paper geometry, used by the page-mode paged viewer to size the
   *  one-page-at-a-time viewport. Falls back to letter portrait. */
  paper?: PaperGeometry;
  /** Surfaced in the paged-viewer footer as a print-style caption. */
  reportTitle?: string;
}): JSX.Element {
  if (output.format === 'pdf') return <PdfViewer url={output.blobUrl} height={height} />;
  // Host-owned embed: headless render + the portal's own toolbar.
  if (output.format === 'html' && output.mode === 'embed') {
    return <EmbedHtmlViewer url={output.blobUrl} html={output.text} />;
  }
  if (output.format === 'html' && output.mode === 'page') {
    // Prefer the server's resolved paper geometry (echoed in
    // x-sage-paper-* response headers) over the SPA's local prop —
    // the server is the source of truth for what was actually rendered.
    const resolvedPaper: PaperGeometry = {
      orientation: output.paperOrientation ?? paper?.orientation ?? 'portrait',
      size: output.paperSize ?? paper?.size ?? 'letter',
    };
    return (
      <PagedHtmlViewer
        url={output.blobUrl}
        paper={resolvedPaper}
        reportTitle={reportTitle ?? 'Report'}
      />
    );
  }
  if (output.format === 'html') return <HtmlViewer url={output.blobUrl} height={height} />;
  if (output.format === 'csv' && output.text) return <CsvViewer text={output.text} />;
  if (output.format === 'json' && output.text) return <JsonViewer text={output.text} />;
  if (output.format === 'xlsx') return <XlsxFallback output={output} />;
  return <BinaryFallback output={output} />;
}

// ─── PDF ───────────────────────────────────────────────────────────────────

function PdfViewer({ url }: { url: string; height: number }): JSX.Element {
  // Fill the stage (height:100%) so the PDF reads full-bleed like the
  // embed report-view, not a small bounded card. `height` is a fallback
  // for callers that don't stretch us.
  return (
    <div
      className="render-viewer render-viewer--pdf"
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <div
        style={{
          padding: '8px 12px',
          background: 'var(--color-surface-1)',
          fontSize: 12,
          flexShrink: 0,
        }}
      >
        PDF preview ·{' '}
        <a href={url} download="report.pdf">
          Download PDF
        </a>{' '}
        to open or print in your PDF viewer.
      </div>
      <iframe
        src={`${url}#toolbar=1&navpanes=0&scrollbar=1&view=FitH`}
        title="Rendered PDF"
        style={{
          width: '100%',
          height: '100%',
          minHeight: 0,
          flex: 1,
          border: 0,
          background: '#1f2329',
          display: 'block',
        }}
      />
    </div>
  );
}

// ─── HTML ──────────────────────────────────────────────────────────────────

/**
 * Live HTML viewer. Auto-sizes the iframe to its content's intrinsic
 * dimensions so the parent stage handles scroll instead of trapping
 * it inside a fixed-size iframe — content reads as part of the
 * page, not as "a document floating in a window".
 *
 * CSAGE-310: the iframe is opaque-origin (no allow-same-origin), so
 * `iframe.contentDocument` throws rather than returning the guest
 * document — reading it here is exactly the same-origin access the
 * sandbox fix removes. Sizing instead comes from the guest-side height
 * reporter (`startHeightReporter` in sage-runtime.ts), which posts
 * `{channel:'sage-report', type:'resize', payload:{height,width}}`
 * unconditionally (on load, on content resize, and on window resize).
 * We authenticate the message by `event.source` (the only identity an
 * opaque-origin frame carries — its `origin` is the literal string
 * "null") and clamp the reported size so a hostile guest can't force a
 * multi-million-pixel layout.
 */
function HtmlViewer({ url, height }: { url: string; height: number }): JSX.Element {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    setSize(null); // new url → new guest document → forget the old size
    function onMessage(e: MessageEvent): void {
      const iframe = iframeRef.current;
      if (!isFrameMessageFrom(e, iframe)) return;
      const msg = parseSageFrameMessage(e.data);
      if (!msg) return;
      // Functional update so a rapid second message never reads a stale
      // `size` from this effect's closure (the effect only re-runs on
      // `url` change, not on every resize).
      setSize((prev) => ({
        w: clampFrameWidth(msg.payload.width, prev?.w ?? 0),
        h: clampFrameHeight(msg.payload.height, height),
      }));
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [url, height]);

  return (
    <div className="render-viewer render-viewer--html">
      <iframe
        ref={iframeRef}
        src={url}
        title="Rendered HTML"
        sandbox={UNTRUSTED_FRAME_SANDBOX}
        style={{
          display: 'block',
          border: 0,
          background: 'transparent',
          // Width: at least the parent's width (so a narrow report
          // doesn't shrink-wrap awkwardly), grow beyond when content
          // is wider so the stage's overflow:auto provides horizontal
          // scroll. Height: track content; fall back to the prop
          // before first measurement to avoid a 0-height flash.
          width: size ? `${Math.max(size.w, 0)}px` : '100%',
          minWidth: '100%',
          height: size ? `${size.h}px` : `${height}px`,
        }}
      />
    </div>
  );
}

// ─── Paged HTML (print preview) ─────────────────────────────────────────────

const PAPER_DIMS_IN: Record<string, [number, number]> = {
  letter: [8.5, 11],
  legal: [8.5, 14],
  a4: [8.27, 11.69],
  tabloid: [11, 17],
};
const SCREEN_DPI = 96;

/**
 * Page-mode viewer. Constrains the iframe to one paper-page at a time,
 * provides prev/next controls, and a chrome footer with page info +
 * generated timestamp. The iframe shows the full rendered HTML; we
 * scroll it page-by-page via contentWindow.scrollTo, so total content
 * height divided by page-height tells us how many pages there are.
 *
 * This is a fast HTML approximation, not a true paginator — content
 * still flows continuously and may split mid-row at break points. For
 * pixel-perfect output use Export → PDF (the actual @page rules win
 * during real printing).
 */
function PagedHtmlViewer({
  url,
  paper,
  reportTitle,
}: {
  url: string;
  paper: PaperGeometry;
  reportTitle: string;
}): JSX.Element {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  // Paged.js can take 20-30 seconds on huge tables (the device-summary
  // 9.5k-row report paginates to 369 pages in ~27s). Without an overlay
  // the user sees a partial-state iframe and may try to interact with
  // it. Cleared the moment the bridge posts {kind:'rendered'}.
  const [paginating, setPaginating] = useState(true);

  const [pwIn, phIn] = PAPER_DIMS_IN[paper.size] ?? PAPER_DIMS_IN.letter!;
  const pageWidthPx = Math.round((paper.orientation === 'landscape' ? phIn : pwIn) * SCREEN_DPI);
  const pageHeightPx = Math.round((paper.orientation === 'landscape' ? pwIn : phIn) * SCREEN_DPI);

  // CSAGE-136: real CSS Paged Media inside the iframe. The page-mode
  // HTML loads Paged.js via CDN; once it's done paginating, the
  // bridge inside posts { source: 'sage-paged', kind: 'rendered',
  // pageCount } back to us. Navigation is element-based (scrollIntoView
  // on the n-th .pagedjs_page) instead of pixel-offset — page 2
  // really is page 2.
  useEffect(() => {
    function onMessage(e: MessageEvent): void {
      const data = e.data as {
        source?: string;
        kind?: string;
        pageCount?: number;
        line?: string;
        fallback?: boolean;
      } | null;
      if (!data || data.source !== 'sage-paged') return;
      if (data.kind === 'log' && typeof data.line === 'string') {
        // Mirror iframe bridge logs into the parent console so the
        // user doesn't have to switch devtools frame context.
        // eslint-disable-next-line no-console
        console.log('[sage-paged]', data.line);
        return;
      }
      if (data.kind === 'rendered' && typeof data.pageCount === 'number') {
        // eslint-disable-next-line no-console
        console.log(
          '[sage-paged] viewer received rendered: pageCount=',
          data.pageCount,
          'fallback=',
          !!data.fallback,
        );
        setPageCount(Math.max(1, data.pageCount));
        setCurrentPage(1);
        setPaginating(false);
      }
    }
    window.addEventListener('message', onMessage);
    setPaginating(true);
    return () => window.removeEventListener('message', onMessage);
  }, [url]);

  function goTo(n: number): void {
    const target = Math.max(1, Math.min(pageCount, n));
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage({ source: 'sage-paged-host', kind: 'goto', page: target }, '*');
    setCurrentPage(target);
  }

  const generatedNow = new Date();

  return (
    <div className="paged-viewer">
      {/* Top toolbar: full PDF-viewer-style nav — first / prev / counter / next / last. */}
      <div className="paged-viewer-toolbar">
        <button
          type="button"
          className="paged-viewer-nav"
          onClick={() => goTo(1)}
          disabled={currentPage <= 1}
          aria-label="First page"
          title="First page"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M11 4L7 8l4 4" />
            <path d="M5 4v8" />
          </svg>
        </button>
        <button
          type="button"
          className="paged-viewer-nav"
          onClick={() => goTo(currentPage - 1)}
          disabled={currentPage <= 1}
          aria-label="Previous page"
          title="Previous page"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10 4L6 8l4 4" />
          </svg>
        </button>
        <span className="paged-viewer-counter">
          {paginating ? (
            <em style={{ fontStyle: 'normal', color: 'var(--color-text-tertiary)' }}>
              Paginating…
            </em>
          ) : (
            <>
              Page <strong>{currentPage}</strong> of {pageCount}
            </>
          )}
        </span>
        <button
          type="button"
          className="paged-viewer-nav"
          onClick={() => goTo(currentPage + 1)}
          disabled={currentPage >= pageCount}
          aria-label="Next page"
          title="Next page"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 4l4 4-4 4" />
          </svg>
        </button>
        <button
          type="button"
          className="paged-viewer-nav"
          onClick={() => goTo(pageCount)}
          disabled={currentPage >= pageCount}
          aria-label="Last page"
          title="Last page"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 4l4 4-4 4" />
            <path d="M11 4v8" />
          </svg>
        </button>
      </div>

      {/* Paper viewport — fixed size, iframe scrolls inside. */}
      <div
        className="paged-viewer-stage"
        style={{ width: pageWidthPx, height: pageHeightPx, position: 'relative' }}
      >
        <iframe
          ref={iframeRef}
          src={url}
          title="Paged HTML preview"
          sandbox={UNTRUSTED_FRAME_SANDBOX}
          scrolling="no"
          style={{
            width: '100%',
            height: '100%',
            border: 0,
            background: '#ffffff',
            display: 'block',
            // Hide the iframe's mid-pagination flash. Bridge clears
            // `paginating` once Paged.js dispatches rendered.
            visibility: paginating ? 'hidden' : 'visible',
          }}
        />
        {paginating && (
          <div
            aria-live="polite"
            aria-busy="true"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 14,
              background: '#ffffff',
              color: 'var(--color-text-secondary)',
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              style={{
                animation: 'spin 0.9s linear infinite',
                color: 'var(--color-accent, #6366f1)',
              }}
            >
              <path d="M21 12a9 9 0 11-6.219-8.56" strokeLinecap="round" />
            </svg>
            <div
              style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, letterSpacing: '0.02em' }}
            >
              Paginating report…
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-tertiary)',
                maxWidth: 320,
                textAlign: 'center',
                lineHeight: 1.4,
              }}
            >
              Building paper-faithful page breaks. Large reports can take 20–30 seconds.
            </div>
          </div>
        )}
      </div>

      {/* Bottom chrome: print-style caption — visible to the user but
          not part of the artifact. The actual artifact's per-page
          header/footer comes from the audience-policy overlay (or the
          PDF export's @page running margins). */}
      <div className="paged-viewer-footer">
        <span>{reportTitle}</span>
        <span aria-hidden="true">·</span>
        <span>Generated {generatedNow.toLocaleString()}</span>
        <span aria-hidden="true">·</span>
        <span>
          Page {currentPage} of {pageCount}
        </span>
      </div>
    </div>
  );
}

// ─── CSV ───────────────────────────────────────────────────────────────────

function CsvViewer({ text }: { text: string }): JSX.Element {
  const { headers, rows } = useMemo(() => parseCsv(text), [text]);
  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const sorted = useMemo(() => {
    if (sortCol === null) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const va = a[sortCol] ?? '';
      const vb = b[sortCol] ?? '';
      const na = Number(va);
      const nb = Number(vb);
      const cmp =
        Number.isFinite(na) && Number.isFinite(nb) ? na - nb : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortCol, sortDir]);

  function clickHeader(i: number): void {
    if (sortCol === i) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(i);
      setSortDir('asc');
    }
  }

  return (
    <div className="render-viewer render-viewer--csv">
      <div className="render-viewer-meta">
        <span>
          {rows.length.toLocaleString()} rows · {headers.length} columns
        </span>
      </div>
      <div className="render-viewer-table-wrap">
        <table className="render-viewer-table">
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th
                  key={i}
                  onClick={() => clickHeader(i)}
                  aria-sort={
                    sortCol === i ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                  }
                  style={{ cursor: 'pointer' }}
                >
                  {h || `col ${i + 1}`}
                  {sortCol === i && (
                    <span className="sort-caret"> {sortDir === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.slice(0, 500).map((row, i) => (
              <tr key={i}>
                {headers.map((_, j) => (
                  <td key={j}>{row[j] ?? ''}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 500 && (
        <div className="render-viewer-overflow">
          … {(rows.length - 500).toLocaleString()} more rows hidden for inline preview. Download to
          see all rows.
        </div>
      )}
    </div>
  );
}

function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  // RFC-4180-ish — handles quoted fields and embedded commas / quotes.
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      continue;
    }
    if (c === ',') {
      row.push(field);
      field = '';
      continue;
    }
    if (c === '\n') {
      row.push(field);
      field = '';
      rows.push(row);
      row = [];
      continue;
    }
    if (c === '\r') continue;
    field += c;
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  const headers = rows.shift() ?? [];
  return { headers, rows };
}

// ─── JSON ──────────────────────────────────────────────────────────────────

function JsonViewer({ text }: { text: string }): JSX.Element {
  const parsed = useMemo(() => {
    try {
      return { ok: true as const, value: JSON.parse(text) as unknown };
    } catch (err) {
      return { ok: false as const, error: (err as Error).message };
    }
  }, [text]);

  if (!parsed.ok) {
    return (
      <div className="render-viewer render-viewer--json">
        <div className="alert alert--warning">
          Could not parse JSON: {parsed.error}. Showing raw text below.
        </div>
        <pre className="render-viewer-raw">{text}</pre>
      </div>
    );
  }

  return (
    <div className="render-viewer render-viewer--json">
      <div className="render-viewer-meta">
        <span>{(text.length / 1024).toFixed(1)} KB</span>
      </div>
      <div className="render-viewer-json-tree">
        <JsonNode name="" value={parsed.value} defaultOpen />
      </div>
    </div>
  );
}

function JsonNode({
  name,
  value,
  defaultOpen = false,
}: {
  name: string;
  value: unknown;
  defaultOpen?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  if (value === null)
    return <LeafLine name={name} render={<span className="json-null">null</span>} />;
  if (typeof value === 'boolean')
    return <LeafLine name={name} render={<span className="json-bool">{String(value)}</span>} />;
  if (typeof value === 'number')
    return (
      <LeafLine name={name} render={<span className="json-num">{value.toLocaleString()}</span>} />
    );
  if (typeof value === 'string')
    return <LeafLine name={name} render={<span className="json-str">"{value}"</span>} />;
  if (Array.isArray(value)) {
    return (
      <div className="json-block">
        <button type="button" className="json-toggle" onClick={() => setOpen(!open)}>
          <span className="json-caret">{open ? '▾' : '▸'}</span>
          {name ? <span className="json-key">{name}:</span> : null}
          <span className="json-muted">[{value.length}]</span>
        </button>
        {open && (
          <div className="json-children">
            {value.slice(0, 50).map((v, i) => (
              <JsonNode key={i} name={String(i)} value={v} />
            ))}
            {value.length > 50 && (
              <div className="json-muted">… {value.length - 50} more items</div>
            )}
          </div>
        )}
      </div>
    );
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj);
    return (
      <div className="json-block">
        <button type="button" className="json-toggle" onClick={() => setOpen(!open)}>
          <span className="json-caret">{open ? '▾' : '▸'}</span>
          {name ? <span className="json-key">{name}:</span> : null}
          <span className="json-muted">{`{${keys.length}}`}</span>
        </button>
        {open && (
          <div className="json-children">
            {keys.map((k) => (
              <JsonNode key={k} name={k} value={obj[k]} />
            ))}
          </div>
        )}
      </div>
    );
  }
  return <LeafLine name={name} render={<span>{String(value)}</span>} />;
}

function LeafLine({ name, render }: { name: string; render: JSX.Element }): JSX.Element {
  return (
    <div className="json-leaf">
      {name ? <span className="json-key">{name}:</span> : null}
      {render}
    </div>
  );
}

// ─── XLSX (fallback) ───────────────────────────────────────────────────────

function XlsxFallback({ output }: { output: RenderedOutput }): JSX.Element {
  return (
    <div className="render-viewer render-viewer--xlsx">
      <div className="render-viewer-fallback">
        <div className="render-viewer-fallback-icon" aria-hidden="true">
          <svg
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          >
            <rect x="6" y="4" width="28" height="32" rx="2" />
            <path d="M13 14 L27 14 M13 20 L27 20 M13 26 L27 26" />
            <path d="M20 10 L20 32" />
          </svg>
        </div>
        <h3>XLSX rendered</h3>
        <p className="text-dim text-sm">
          {(output.byteLength / 1024).toFixed(1)} KB · {output.rowCount.toLocaleString()} rows ·{' '}
          {output.durationMs} ms
        </p>
        <p className="text-sm">
          Open in your spreadsheet app for full fidelity (multi-sheet, formulas, subtotals, outline
          groups).
        </p>
        <a className="btn btn--primary btn--sm" href={output.blobUrl} download>
          Download .xlsx
        </a>
      </div>
    </div>
  );
}

function BinaryFallback({ output }: { output: RenderedOutput }): JSX.Element {
  return (
    <div className="render-viewer render-viewer--binary">
      <div className="render-viewer-fallback">
        <h3>Output ready</h3>
        <p className="text-sm text-dim">
          {output.contentType} · {(output.byteLength / 1024).toFixed(1)} KB
        </p>
        <a className="btn btn--primary btn--sm" href={output.blobUrl} download>
          Download
        </a>
      </div>
    </div>
  );
}
